// src/api/auth.ts
import { API_BASE_URL, handleJsonResponse } from "./client";

export interface AuthUser {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT" | "REFERRER";
  emailVerified: boolean;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export interface MfaChallengeResponse {
  mfaRequired: true;
  mfaToken: string;
}

export interface VerifyEmailResponse {
  success?: boolean;
  message?: string;
}

const USER_KEY = "authUser";

let memoryAccessToken: string | null = null;

export function getStoredAccessToken(): string | null {
  return memoryAccessToken;
}

export function getStoredRefreshToken(): string | null {
  return null;
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function storeAuthData(data: AuthResponse): void {
  memoryAccessToken = data.accessToken;
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
}

export function clearAuthData(): void {
  memoryAccessToken = null;
  localStorage.removeItem(USER_KEY);
}

export async function registerApi(email: string, password: string): Promise<void> {
  const res = await fetch(`/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password })
  });
  await handleJsonResponse<unknown>(res);
}

export async function loginApi(
  email: string,
  password: string
): Promise<AuthResponse | MfaChallengeResponse> {
  const res = await fetch(`/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password })
  });
  return handleJsonResponse<AuthResponse | MfaChallengeResponse>(res);
}

export async function verifyEmailApi(token: string): Promise<VerifyEmailResponse> {
  const res = await fetch(`/auth/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token })
  });
  return handleJsonResponse<VerifyEmailResponse>(res);
}

/**
 * NOTE: Backend /auth/refresh now returns AuthResponse
 * and rotates the refresh token.
 */
export async function refreshTokenApi(refreshToken?: string | null): Promise<AuthResponse> {
  const body = refreshToken ? JSON.stringify({ refreshToken }) : undefined;
  const res = await fetch(`/auth/refresh`, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    credentials: "include",
    body
  });
  return handleJsonResponse<AuthResponse>(res);
}

export async function logoutApi(refreshToken: string | null): Promise<void> {
  const body = refreshToken ? JSON.stringify({ refreshToken }) : undefined;
  const res = await fetch(`/auth/logout`, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    credentials: "include",
    body
  });
  await handleJsonResponse<unknown>(res);
}

export async function loginWithGoogleApi(
  idToken: string
): Promise<AuthResponse | MfaChallengeResponse> {
  const res = await fetch(`/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ idToken })
  });
  return handleJsonResponse<AuthResponse | MfaChallengeResponse>(res);
}

export async function verifyMfaTotpApi(mfaToken: string, code: string): Promise<AuthResponse> {
  const res = await fetch(`/auth/mfa/totp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ mfaToken, code })
  });
  return handleJsonResponse<AuthResponse>(res);
}

export async function forgotPasswordApi(email: string): Promise<{ message?: string }> {
  const res = await fetch(`/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email })
  });
  return handleJsonResponse<{ message?: string }>(res);
}

export async function resetPasswordApi(
  email: string,
  code: string,
  newPassword: string
): Promise<{ message?: string }> {
  const res = await fetch(`/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, code, newPassword })
  });
  return handleJsonResponse<{ message?: string }>(res);
}
